import React, { useState } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  Modal,
  ScrollView,
  Alert,
  KeyboardAvoidingView,
  Platform
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';

/**
 * Web-compatible modal component for adding a new restaurant
 * Uses text inputs for coordinates instead of a map
 */
const AddRestaurantModal = ({
  visible,
  onClose,
  onSubmit,
  currentLocation
}) => {
  const [formData, setFormData] = useState({
    name: '',
    cuisine: '',
    phone: '',
    website: '',
    description: '',
    latitude: currentLocation?.latitude?.toString() || '30.2672',
    longitude: currentLocation?.longitude?.toString() || '-97.7431',
    address: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async () => {
    // Basic validation
    if (!formData.name.trim()) {
      Alert.alert('Error', 'Restaurant name is required');
      return;
    }

    const latitude = parseFloat(formData.latitude);
    const longitude = parseFloat(formData.longitude);

    if (isNaN(latitude) || isNaN(longitude)) {
      Alert.alert('Error', 'Please enter valid coordinates');
      return;
    }

    if (!formData.cuisine.trim()) {
      Alert.alert('Error', 'Cuisine type is required');
      return;
    }

    setIsSubmitting(true);

    try {
      // Create restaurant object
      const restaurantData = {
        name: formData.name.trim(),
        address: formData.address.trim() || `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`,
        cuisine: formData.cuisine.trim(),
        phone: formData.phone.trim() || null,
        website: formData.website.trim() || null,
        description: formData.description.trim() || null,
        latitude: latitude,
        longitude: longitude,
        isPartner: false, // New restaurants are non-partners by default
        rating: 0,
        userRatingCount: 0,
        price: '',
        imgUrl: null,
        thumbnail: null,
        isDailySpecialsConfirmed: false,
        venueDescription: formData.description.trim() || null,
        googlePlaceId: null,
        googleTypes: ['restaurant'],
        membershipTiers: null
      };

      await onSubmit(restaurantData);
      
      // Reset form
      setFormData({
        name: '',
        cuisine: '',
        phone: '',
        website: '',
        description: '',
        latitude: currentLocation?.latitude?.toString() || '30.2672',
        longitude: currentLocation?.longitude?.toString() || '-97.7431',
        address: ''
      });
      
      onClose();
    } catch (error) {
      Alert.alert('Error', 'Failed to add restaurant. Please try again.');
      console.error('Error adding restaurant:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setFormData({
        name: '',
        cuisine: '',
        phone: '',
        website: '',
        description: '',
        latitude: currentLocation?.latitude?.toString() || '30.2672',
        longitude: currentLocation?.longitude?.toString() || '-97.7431',
        address: ''
      });
      onClose();
    }
  };

  return (
    <Modal
      visible={visible}
      animationType="slide"
      presentationStyle="pageSheet"
      onRequestClose={handleClose}
    >
      <KeyboardAvoidingView 
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        style={{ flex: 1, backgroundColor: '#FFFFFF' }}
      >
        {/* Header */}
        <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', padding: 16, borderBottomWidth: 1, borderBottomColor: '#e0e0e0' }}>
          <TouchableOpacity onPress={handleClose} disabled={isSubmitting}>
            <Text style={{ fontSize: 18, fontFamily: 'AbhayaLibre_400Regular, serif', color: '#6B4C3B' }}>
              {isSubmitting ? 'Adding...' : 'Cancel'}
            </Text>
          </TouchableOpacity>
          <Text style={{ fontSize: 18, fontFamily: 'AbhayaLibre_700Bold, serif', color: '#000000' }}>
            Add Restaurant
          </Text>
          <View style={{ width: 60 }} />
        </View>

        <ScrollView style={{ flex: 1, padding: 16 }}>
          <Text style={{ fontSize: 14, fontFamily: 'AbhayaLibre_400Regular, serif', color: '#666', marginBottom: 24, textAlign: 'center' }}>
            Help us add this restaurant to Disgo! All fields marked with * are required.
          </Text>

          {/* Restaurant Name */}
          <View style={{ marginBottom: 16 }}>
            <Text style={{ fontSize: 14, fontFamily: 'AbhayaLibre_700Bold, serif', color: '#000000', marginBottom: 8 }}>
              Restaurant Name *
            </Text>
            <TextInput
              style={{ borderWidth: 1, borderColor: '#d0d0d0', borderRadius: 8, padding: 12, fontSize: 16, fontFamily: 'AbhayaLibre_400Regular, serif', color: '#000000' }}
              placeholder="Enter restaurant name"
              value={formData.name}
              onChangeText={(value) => handleInputChange('name', value)}
              editable={!isSubmitting}
            />
          </View>

          {/* Location Coordinates */}
          <View style={{ marginBottom: 16 }}>
            <Text style={{ fontSize: 14, fontFamily: 'AbhayaLibre_700Bold, serif', color: '#000000', marginBottom: 8 }}>
              Location *
            </Text>
            <Text style={{ fontSize: 12, fontFamily: 'AbhayaLibre_400Regular, serif', color: '#666', marginBottom: 8 }}>
              Enter the restaurant's coordinates
            </Text>
            <View style={{ flexDirection: 'row', gap: 8 }}>
              <View style={{ flex: 1 }}>
                <Text style={{ fontSize: 12, fontFamily: 'AbhayaLibre_400Regular, serif', color: '#666', marginBottom: 4 }}>
                  Latitude
                </Text>
                <TextInput
                  style={{ borderWidth: 1, borderColor: '#d0d0d0', borderRadius: 8, padding: 12, fontSize: 16, fontFamily: 'AbhayaLibre_400Regular, serif', color: '#000000' }}
                  placeholder="30.2672"
                  value={formData.latitude}
                  onChangeText={(value) => handleInputChange('latitude', value)}
                  editable={!isSubmitting}
                  keyboardType="numeric"
                />
              </View>
              <View style={{ flex: 1 }}>
                <Text style={{ fontSize: 12, fontFamily: 'AbhayaLibre_400Regular, serif', color: '#666', marginBottom: 4 }}>
                  Longitude
                </Text>
                <TextInput
                  style={{ borderWidth: 1, borderColor: '#d0d0d0', borderRadius: 8, padding: 12, fontSize: 16, fontFamily: 'AbhayaLibre_400Regular, serif', color: '#000000' }}
                  placeholder="-97.7431"
                  value={formData.longitude}
                  onChangeText={(value) => handleInputChange('longitude', value)}
                  editable={!isSubmitting}
                  keyboardType="numeric"
                />
              </View>
            </View>
          </View>

          {/* Address */}
          <View style={{ marginBottom: 16 }}>
            <Text style={{ fontSize: 14, fontFamily: 'AbhayaLibre_700Bold, serif', color: '#000000', marginBottom: 8 }}>
              Address
            </Text>
            <TextInput
              style={{ borderWidth: 1, borderColor: '#d0d0d0', borderRadius: 8, padding: 12, fontSize: 16, fontFamily: 'AbhayaLibre_400Regular, serif', color: '#000000' }}
              placeholder="Enter restaurant address"
              value={formData.address}
              onChangeText={(value) => handleInputChange('address', value)}
              editable={!isSubmitting}
            />
          </View>

          {/* Cuisine Type */}
          <View style={{ marginBottom: 16 }}>
            <Text style={{ fontSize: 14, fontFamily: 'AbhayaLibre_700Bold, serif', color: '#000000', marginBottom: 8 }}>
              Cuisine Type *
            </Text>
            <TextInput
              style={{ borderWidth: 1, borderColor: '#d0d0d0', borderRadius: 8, padding: 12, fontSize: 16, fontFamily: 'AbhayaLibre_400Regular, serif', color: '#000000' }}
              placeholder="e.g., Italian, Mexican, American, etc."
              value={formData.cuisine}
              onChangeText={(value) => handleInputChange('cuisine', value)}
              editable={!isSubmitting}
            />
          </View>

          {/* Phone Number */}
          <View style={{ marginBottom: 16 }}>
            <Text style={{ fontSize: 14, fontFamily: 'AbhayaLibre_700Bold, serif', color: '#000000', marginBottom: 8 }}>
              Phone Number
            </Text>
            <TextInput
              style={{ borderWidth: 1, borderColor: '#d0d0d0', borderRadius: 8, padding: 12, fontSize: 16, fontFamily: 'AbhayaLibre_400Regular, serif', color: '#000000' }}
              placeholder="(555) 123-4567"
              value={formData.phone}
              onChangeText={(value) => handleInputChange('phone', value)}
              editable={!isSubmitting}
              keyboardType="phone-pad"
            />
          </View>

          {/* Website */}
          <View style={{ marginBottom: 16 }}>
            <Text style={{ fontSize: 14, fontFamily: 'AbhayaLibre_700Bold, serif', color: '#000000', marginBottom: 8 }}>
              Website
            </Text>
            <TextInput
              style={{ borderWidth: 1, borderColor: '#d0d0d0', borderRadius: 8, padding: 12, fontSize: 16, fontFamily: 'AbhayaLibre_400Regular, serif', color: '#000000' }}
              placeholder="https://www.restaurant.com"
              value={formData.website}
              onChangeText={(value) => handleInputChange('website', value)}
              editable={!isSubmitting}
              keyboardType="url"
              autoCapitalize="none"
            />
          </View>

          {/* Description */}
          <View style={{ marginBottom: 24 }}>
            <Text style={{ fontSize: 14, fontFamily: 'AbhayaLibre_700Bold, serif', color: '#000000', marginBottom: 8 }}>
              Description
            </Text>
            <TextInput
              style={{ borderWidth: 1, borderColor: '#d0d0d0', borderRadius: 8, padding: 12, fontSize: 16, fontFamily: 'AbhayaLibre_400Regular, serif', color: '#000000', minHeight: 80 }}
              placeholder="Tell us about this restaurant..."
              value={formData.description}
              onChangeText={(value) => handleInputChange('description', value)}
              editable={!isSubmitting}
              multiline
              numberOfLines={3}
            />
          </View>
        </ScrollView>

        {/* Submit Button */}
        <View style={{ padding: 16, borderTopWidth: 1, borderTopColor: '#e0e0e0' }}>
          <TouchableOpacity
            onPress={handleSubmit}
            disabled={isSubmitting}
            style={{
              paddingVertical: 12,
              paddingHorizontal: 24,
              borderRadius: 8,
              backgroundColor: isSubmitting ? '#d0d0d0' : '#6B4C3B',
              alignItems: 'center'
            }}
          >
            <Text style={{
              textAlign: 'center',
              fontSize: 16,
              fontFamily: 'AbhayaLibre_700Bold, serif',
              color: isSubmitting ? '#666' : '#FFFFFF'
            }}>
              {isSubmitting ? 'Adding Restaurant...' : 'Add Restaurant'}
            </Text>
          </TouchableOpacity>
        </View>
      </KeyboardAvoidingView>
    </Modal>
  );
};

export default AddRestaurantModal;

